import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '@layout/Layout';
import * as AssessmentService from '@services/AssessmentService';
import { useAppTheme } from '@contexts/ThemeContext';
import { Plus, Edit, PlusCircle, MoreVertical } from 'lucide-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
} from '@mui/material';
import Spinner from './components/Spinner';
import './ManageAssessments.css';

type TabType = 'published' | 'draft' | 'review';

const ManageAssessments: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab') as TabType | null;

  const [activeTab, setActiveTab] = useState<TabType>(tabParam || 'published');
  const [publishedAssessments, setPublishedAssessments] = useState<AssessmentService.Assessment[]>([]);
  const [draftAssessments, setDraftAssessments] = useState<AssessmentService.Assessment[]>([]);
  const [reviewAssessments, setReviewAssessments] = useState<AssessmentService.Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAssessment, setSelectedAssessment] = useState<AssessmentService.Assessment | null>(null);

  // Published assessment edit modal state
  const [publishedEditModalOpen, setPublishedEditModalOpen] = useState(false);
  const [publishedEditData, setPublishedEditData] = useState({
    startTime: '',
    endTime: ''
  });

  // Draft assessment edit modal state
  const [draftEditModalOpen, setDraftEditModalOpen] = useState(false);
  const [draftEditData, setDraftEditData] = useState({
    assessmentTitle: '',
    creator: '',
    goal: '',
    course: '',
    startTime: '',
    endTime: '',
    status: ''
  });

  // Delete confirmation modal state
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState<AssessmentService.Assessment | null>(null);

  useEffect(() => {
    fetchAssessments();
  }, []);

  useEffect(() => {
    // Update active tab when URL parameter changes
    if (tabParam && (tabParam === 'published' || tabParam === 'draft' || tabParam === 'review')) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const fetchAssessments = async () => {
    setLoading(true);
    setError(null);

    try {
      const [publishedResponse, draftResponse, reviewResponse] = await Promise.all([
        AssessmentService.getAssessments(),
        AssessmentService.getDraftAssessments(),
        AssessmentService.getReviewAssessments()
      ]);

      if (publishedResponse.success && publishedResponse.data) {
        setPublishedAssessments(publishedResponse.data);
      } else {
        console.error('Failed to fetch published assessments:', publishedResponse.error?.message);
      }

      if (draftResponse.success && draftResponse.data) {
        setDraftAssessments(draftResponse.data);
      } else {
        console.error('Failed to fetch draft assessments:', draftResponse.error?.message);
      }

      if (reviewResponse.success && reviewResponse.data) {
        setReviewAssessments(reviewResponse.data);
      } else {
        console.error('Failed to fetch review assessments:', reviewResponse.error?.message);
      }
    } catch (err: any) {
      console.error('Error fetching assessments:', err);
      setError(err.message || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleViewResults = (assessmentId: number) => {
    navigate(`/assessment-results/${assessmentId}`);
  };

  const handleCreateAssessment = () => {
    navigate('/create-assessment');
  };

  const handleFinishAssessment = (assessmentId: number) => {
    navigate(`/module-assessment-mapping?assessmentId=${assessmentId}`);
  };

  const handleCreateQuestions = (assessmentId: number, assessmentTypeId?: number) => {
    // Navigate to create-question page
    // The CreateQuestion component handles both regular and 360 feedback questions
    // based on assessmentTypeId (shows Create360Question when assessmentTypeId === 3)
    navigate(`/create-question?assessmentId=${assessmentId}`);
  };

  const getCurrentAssessments = () => {
    switch (activeTab) {
      case 'published':
        return publishedAssessments;
      case 'draft':
        return draftAssessments;
      case 'review':
        return reviewAssessments;
      default:
        return [];
    }
  };

  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return 'N/A';
    const date = new Date(dateTime);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTimeForInput = (dateTime?: string) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    // Format as YYYY-MM-DDTHH:mm for datetime-local input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, assessment: AssessmentService.Assessment) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedAssessment(assessment);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditPublishedClick = () => {
    if (selectedAssessment) {
      setPublishedEditData({
        startTime: formatDateTimeForInput(selectedAssessment.startTime),
        endTime: formatDateTimeForInput(selectedAssessment.endTime)
      });
      setPublishedEditModalOpen(true);
    }
    handleMenuClose();
  };

  const handleEditDraftClick = (assessment: AssessmentService.Assessment) => {
    setSelectedAssessment(assessment);
    setDraftEditData({
      assessmentTitle: assessment.assessmentTitle || '',
      creator: assessment.creator || '',
      goal: assessment.goal || '',
      course: assessment.course || '',
      startTime: formatDateTimeForInput(assessment.startTime),
      endTime: formatDateTimeForInput(assessment.endTime),
      status: 'draft'
    });
    setDraftEditModalOpen(true);
  };

  const handlePublishedEditSubmit = async () => {
    if (!selectedAssessment) return;

    try {
      const updateData: AssessmentService.UpdatePublishedAssessmentData = {
        startTime: publishedEditData.startTime || undefined,
        endTime: publishedEditData.endTime || undefined
      };

      const response = await AssessmentService.updatePublishedAssessment(
        selectedAssessment.assessmentId,
        updateData
      );

      if (response.success) {
        setPublishedEditModalOpen(false);
        fetchAssessments(); // Refresh the assessments list
      } else {
        alert(response.error?.message || 'Failed to update assessment');
      }
    } catch (error: any) {
      console.error('Error updating published assessment:', error);
      alert(error.message || 'Failed to update assessment');
    }
  };

  const handleDraftEditSubmit = async () => {
    if (!selectedAssessment) return;

    try {
      const updateData: AssessmentService.UpdateDraftAssessmentData = {
        assessmentTitle: draftEditData.assessmentTitle || undefined,
        creator: draftEditData.creator || undefined,
        goal: draftEditData.goal || undefined,
        course: draftEditData.course || undefined,
        startTime: draftEditData.startTime || undefined,
        endTime: draftEditData.endTime || undefined,
        status: draftEditData.status || undefined
      };

      const response = await AssessmentService.updateDraftAssessment(
        selectedAssessment.assessmentId,
        updateData
      );

      if (response.success) {
        setDraftEditModalOpen(false);
        fetchAssessments(); // Refresh the assessments list
      } else {
        alert(response.error?.message || 'Failed to update assessment');
      }
    } catch (error: any) {
      console.error('Error updating draft assessment:', error);
      alert(error.message || 'Failed to update assessment');
    }
  };

  const handleDeleteClick = () => {
    if (selectedAssessment) {
      setAssessmentToDelete(selectedAssessment);
      setDeleteConfirmModalOpen(true);
    }
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!assessmentToDelete) return;

    try {
      const response = await AssessmentService.deleteAssessment(assessmentToDelete.assessmentId);

      if (response.success) {
        setDeleteConfirmModalOpen(false);
        setAssessmentToDelete(null);
        fetchAssessments(); // Refresh the assessments list
      } else {
        alert(response.error?.message || 'Failed to delete assessment');
      }
    } catch (error: any) {
      console.error('Error deleting assessment:', error);
      alert(error.message || 'Failed to delete assessment');
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmModalOpen(false);
    setAssessmentToDelete(null);
  };

  const renderAssessmentCard = (assessment: AssessmentService.Assessment) => {
    return (
      <div key={assessment.assessmentId} className="assessment-card">
        <div className="assessment-card-header">
          <h3 className="assessment-title">{assessment.assessmentTitle}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {activeTab === 'review' && (
              <span className="status-badge status-review">Review</span>
            )}
            {(activeTab === 'published' || activeTab === 'draft') && (
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, assessment)}
                sx={{ color: 'inherit' }}
              >
                <MoreVertical size={20} />
              </IconButton>
            )}
          </div>
        </div>
        <div className="assessment-card-body">
          <div className="assessment-details">
            <div className="detail-item">
              <span className="detail-label">Start Time:</span>
              <span className="detail-value">{formatDateTime(assessment.startTime)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">End Time:</span>
              <span className="detail-value">{formatDateTime(assessment.endTime)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Goal:</span>
              <span className="detail-value">{assessment.goal || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Creator:</span>
              <span className="detail-value">{assessment.creator || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Course:</span>
              <span className="detail-value">{assessment.course || 'N/A'}</span>
            </div>
          </div>
        </div>
        <div className="assessment-card-footer">
          {activeTab === 'published' && (
            <button
              className="btn-view-results"
              onClick={() => handleViewResults(assessment.assessmentId)}
            >
              View Results
            </button>
          )}
          {activeTab === 'draft' && (
            <div className="draft-actions">
              <button
                className="btn-finish-assessment"
                onClick={() => handleFinishAssessment(assessment.assessmentId)}
              >
                <Edit size={18} />
                <span>Modules</span>
              </button>
              <button
                className="btn-create-questions"
                onClick={() => handleCreateQuestions(assessment.assessmentId, assessment.assessment_type_id)}
              >
                <PlusCircle size={18} />
                <span>Questions</span>
              </button>
            </div>
          )}
          {activeTab === 'review' && (
            <button
              className="btn-view-results"
              onClick={() => handleViewResults(assessment.assessmentId)}
            >
              View Details
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="manage-assessments" data-theme={isDarkMode ? 'dark' : 'light'}>
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="manage-assessments" data-theme={isDarkMode ? 'dark' : 'light'}>
          <div className="error-message">{error}</div>
          <button className="btn-retry" onClick={fetchAssessments}>
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  const currentAssessments = getCurrentAssessments();

  return (
    <Layout>
      <div className="manage-assessments" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="page-header">
          <div>
            <h1 className="page-title">Manage Assessments</h1>
            <p className="page-subtitle">View and manage all assessments</p>
          </div>
          <button className="btn-create-assessment" onClick={handleCreateAssessment}>
            <Plus size={20} />
            <span>Create Assessment</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'published' ? 'tab-active' : ''}`}
              onClick={() => setActiveTab('published')}
            >
              Published
              {publishedAssessments.length > 0 && (
                <span className="tab-count">{publishedAssessments.length}</span>
              )}
            </button>
            <button
              className={`tab ${activeTab === 'draft' ? 'tab-active' : ''}`}
              onClick={() => setActiveTab('draft')}
            >
              Draft
              {draftAssessments.length > 0 && (
                <span className="tab-count">{draftAssessments.length}</span>
              )}
            </button>
            <button
              className={`tab ${activeTab === 'review' ? 'tab-active' : ''}`}
              onClick={() => setActiveTab('review')}
            >
              Review
              {reviewAssessments.length > 0 && (
                <span className="tab-count">{reviewAssessments.length}</span>
              )}
            </button>
          </div>
        </div>

        {/* Assessments List */}
        <div className="assessments-section">
          <h2 className="section-title">
            {activeTab === 'published' && 'Published Assessments'}
            {activeTab === 'draft' && 'Draft Assessments'}
            {activeTab === 'review' && 'Assessments Under Review'}
          </h2>

          {currentAssessments.length === 0 ? (
            <div className="empty-state">
              <p>No {activeTab} assessments found</p>
            </div>
          ) : (
            <div className="assessments-grid">
              {currentAssessments.map(renderAssessmentCard)}
            </div>
          )}
        </div>

        {/* Three-dots menu for published and draft assessments */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={activeTab === 'published' ? handleEditPublishedClick : () => { if (selectedAssessment) handleEditDraftClick(selectedAssessment); handleMenuClose(); }}>
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
          <MenuItem onClick={() => {
            if (selectedAssessment) {
              navigate('/manage-assessment-eligibility', {
                state: { assessmentId: selectedAssessment.assessmentId, assessmentTitle: selectedAssessment.assessmentTitle }
              });
            }
            handleMenuClose();
          }}>
            Manage Eligibility
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedAssessment) {
              navigate('/assessment-creator/360-feedback/reporting-managers', {
                state: { assessmentId: selectedAssessment.assessmentId, assessmentTitle: selectedAssessment.assessmentTitle }
              });
            }
            handleMenuClose();
          }}>
            Reporting Managers
          </MenuItem>
        </Menu>

        {/* Published Assessment Edit Modal */}
        <Dialog
          open={publishedEditModalOpen}
          onClose={() => setPublishedEditModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Published Assessment</DialogTitle>
          <DialogContent>
            <TextField
              label="Start Time"
              type="datetime-local"
              value={publishedEditData.startTime}
              onChange={(e) => setPublishedEditData({ ...publishedEditData, startTime: e.target.value })}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Time"
              type="datetime-local"
              value={publishedEditData.endTime}
              onChange={(e) => setPublishedEditData({ ...publishedEditData, endTime: e.target.value })}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPublishedEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handlePublishedEditSubmit} variant="contained" color="primary">
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Draft Assessment Edit Modal */}
        <Dialog
          open={draftEditModalOpen}
          onClose={() => setDraftEditModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { maxHeight: '60vh' }
          }}
        >
          <DialogTitle>Edit Draft Assessment</DialogTitle>
          <DialogContent>
            <TextField
              label="Assessment Title"
              value={draftEditData.assessmentTitle}
              onChange={(e) => setDraftEditData({ ...draftEditData, assessmentTitle: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Creator"
              value={draftEditData.creator}
              onChange={(e) => setDraftEditData({ ...draftEditData, creator: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Goal"
              value={draftEditData.goal}
              onChange={(e) => setDraftEditData({ ...draftEditData, goal: e.target.value })}
              fullWidth
              margin="normal"
              multiline
              rows={2}
            />
            <TextField
              label="Course"
              value={draftEditData.course}
              onChange={(e) => setDraftEditData({ ...draftEditData, course: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Start Time"
              type="datetime-local"
              value={draftEditData.startTime}
              onChange={(e) => setDraftEditData({ ...draftEditData, startTime: e.target.value })}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Time"
              type="datetime-local"
              value={draftEditData.endTime}
              onChange={(e) => setDraftEditData({ ...draftEditData, endTime: e.target.value })}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={draftEditData.status}
                onChange={(e) => setDraftEditData({ ...draftEditData, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="review">Review</MenuItem>
                <MenuItem value="ready">Ready (Publish)</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDraftEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleDraftEditSubmit} variant="contained" color="primary">
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteConfirmModalOpen}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{assessmentToDelete?.assessmentTitle}"?
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>Cancel</Button>
            <Button onClick={handleConfirmDelete} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ManageAssessments;
